import axios from 'axios';
import type {
  ClientRemoteTiersEnvoi,
  ClientRemoteTiersEtat,
  CreanceInput,
  DdnsJetonPostesInput,
  DdnsServeurInput,
  DesktopLotInput,
  DesktopVersionInput,
  ReglementInput,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const EXPLICIT_WS_BASE_URL = (process.env.REACT_APP_WS_URL || '').trim();
const EXPLICIT_WS_PORT = Number(process.env.REACT_APP_WS_PORT || '0');
const DEFAULT_WS_PORT = 1903;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const clientPortalApiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

function getRealtimeWebSocketBaseUrl(): string {
  if (EXPLICIT_WS_BASE_URL) {
    return EXPLICIT_WS_BASE_URL.replace(/\/+$/, '');
  }

  if (/^https?:\/\//i.test(API_BASE_URL)) {
    const apiUrl = new URL(API_BASE_URL);
    const wsProtocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsPort =
      EXPLICIT_WS_PORT > 0
        ? EXPLICIT_WS_PORT
        : apiUrl.port
        ? Number(apiUrl.port) + 1
        : DEFAULT_WS_PORT;
    return `${wsProtocol}//${apiUrl.hostname}:${wsPort}`;
  }

  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsPort = EXPLICIT_WS_PORT > 0 ? EXPLICIT_WS_PORT : DEFAULT_WS_PORT;
  return `${wsProtocol}//${window.location.hostname}:${wsPort}`;
}

function buildChatSocketUrl(token?: string | null): string {
  const trimmedToken = (token || '').trim();
  if (!trimmedToken) {
    return '';
  }

  const baseUrl = getRealtimeWebSocketBaseUrl();
  const socketPath = baseUrl.endsWith('/ws/chat') ? '' : '/ws/chat';
  return `${baseUrl}${socketPath}?token=${encodeURIComponent(trimmedToken)}`;
}

// Ajouter le token JWT aux requêtes
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Intercepteur pour gérer les erreurs d'authentification
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

clientPortalApiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('client_portal_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

clientPortalApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('client_portal_token');
      localStorage.removeItem('client_portal_user');
      if (
        window.location.pathname.startsWith('/client') &&
        !window.location.pathname.startsWith('/client/login')
      ) {
        window.location.href = '/client/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  getUsers: () => api.get('/auth/users'),
  getPhones: () => api.get('/auth/phones'),
  sendOTP: (username: string, phoneNumber: string) =>
    api.post('/auth/send-otp', { user_name: username, phone_number: phoneNumber }),
  login: (username: string, password: string, otpCode?: string) =>
    api.post('/auth/login', { user_name: username, password, otp_code: otpCode }),
  changePassword: (username: string, oldPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { user_name: username, old_password: oldPassword, new_password: newPassword }),
  refreshToken: () => api.post('/auth/refresh'),
  ping: () => api.get('/auth/ping'),
  logout: () => api.post('/auth/logout'),
};

// Contrats API
export const contratsApi = {
  getAll: (params?: { search?: string; type_contrat?: string; mode_reglt?: string }) =>
    api.get('/contrats', { params }),
  getById: (id: number) => api.get(`/contrats/${id}`),
  create: (data: ContratInput) => api.post('/contrats', data),
  update: (id: number, data: ContratInput) => api.put(`/contrats/${id}`, data),
  delete: (id: number) => api.delete(`/contrats/${id}`),
  getExpired: (days?: number) => api.get('/contrats/expired', { params: { days } }),
  sendSms: (id: number) => api.post(`/contrats/${id}/send-sms`, {}),
  getSmsHistory: (id: number) => api.get(`/contrats/${id}/sms-history`),
};

// Clients API
export const clientsApi = {
  getAll: (search?: string) => api.get('/clients', { params: { search } }),
  getById: (id: number) => api.get(`/clients/${id}`),
  create: (data: ClientInput) => api.post('/clients', data),
  update: (id: number, data: ClientInput) => api.put(`/clients/${id}`, data),
  delete: (id: number) => api.delete(`/clients/${id}`),
  checkExists: (name: string) => api.get('/clients/check', { params: { name } }),
  sendEmail: (id: number, subject?: string, messageText?: string) =>
    api.post(`/clients/${id}/send-email`, {
      subject: subject || '',
      message_text: messageText || '',
    }),
  sendSms: (id: number, messageText?: string) =>
    api.post(`/clients/${id}/send-sms`, { message_text: messageText || '' }),
  // Remet le mot de passe du portail client a '1' et ferme ses sessions.
  resetPortalAccess: (id: number) => api.post(`/clients/${id}/reset-portal-access`, {}),
  sendBulkEmail: (clientIds?: number[], subject?: string, messageText?: string) =>
    api.post('/clients/send-bulk-email', {
      client_ids: clientIds || [],
      subject: subject || '',
      message_text: messageText || '',
    }),
  sendBulkSms: (clientIds?: number[], messageText?: string) =>
    api.post('/clients/send-bulk-sms', { client_ids: clientIds || [], message_text: messageText || '' }),
  // Tiers du remote-server (Remote Desktop) : etat des envois, et envoi d'une
  // selection (200 clients au plus par appel).
  getRemoteTiers: () => api.get<ClientRemoteTiersEtat>('/clients/remote-tiers'),
  sendToRemote: (clientIds: number[]) =>
    api.post<ClientRemoteTiersEnvoi>('/clients/remote-tiers/envoyer', { client_ids: clientIds }),
};

export const clientPortalApi = {
  login: (identifier: string, password: string) =>
    clientPortalApiClient.post('/client-portal/login', { identifier, password }),
  // Credentials TURN éphémères (même mécanisme que côté staff, jeton portail).
  getIceServers: () => clientPortalApiClient.get('/client-portal/webrtc/ice-servers'),
  getNotificationSummary: () =>
    clientPortalApiClient.get('/client-portal/notifications-summary'),
  getProfile: () => clientPortalApiClient.get('/client-portal/profile'),
  updateProfile: (data: ClientPortalProfileInput) =>
    clientPortalApiClient.put('/client-portal/profile', data),
  changePassword: (oldPassword: string, newPassword: string) =>
    clientPortalApiClient.post('/client-portal/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
    }),
  logout: () => clientPortalApiClient.post('/client-portal/logout'),
  getTickets: () => clientPortalApiClient.get('/client-portal/tickets'),
  createTicket: (data: ClientPortalTicketInput) =>
    clientPortalApiClient.post('/client-portal/tickets', data),
  getChatConversation: () => clientPortalApiClient.get('/client-portal/chat'),
  waitForChatUpdates: (after?: string, supportOnline?: boolean) =>
    clientPortalApiClient.get('/client-portal/chat/updates', {
      params: {
        after: after || '',
        support_online:
          typeof supportOnline === 'boolean' ? String(supportOnline) : '',
      },
      timeout: 30000,
    }),
  sendChatMessage: (messageText: string, attachments?: ChatMessageAttachmentInput[]) =>
    clientPortalApiClient.post('/client-portal/chat/messages', {
      message_text: messageText,
      attachments: attachments || [],
    }),
  sendChatCallCommand: (
    action: 'request' | 'accept' | 'refuse' | 'end',
    conversationId?: number
  ) =>
    clientPortalApiClient.post(`/client-portal/chat/call/${action}`, {
      conversation_id: conversationId || 0,
    }),
  sendChatWebRTCSignal: (
    signalType: 'offer' | 'answer' | 'ice_candidate',
    conversationId: number,
    data: ChatWebRTCSignalInput
  ) =>
    clientPortalApiClient.post(`/client-portal/chat/webrtc/${signalType}`, {
      conversation_id: conversationId,
      sdp: data.sdp || '',
      candidate: data.candidate || '',
      sdp_mid: data.sdp_mid || '',
      sdp_mline_index:
        typeof data.sdp_mline_index === 'number' ? data.sdp_mline_index : -1,
    }),
  downloadChatAttachment: (messageId: number, attachmentId: number) =>
    clientPortalApiClient.get(
      `/client-portal/chat/messages/${messageId}/attachments/${attachmentId}`,
      { responseType: 'blob' }
    ),
  downloadTicketAttachment: (ticketId: number, attachmentId: number) =>
    clientPortalApiClient.get(
      `/client-portal/tickets/${ticketId}/attachments/${attachmentId}`,
      { responseType: 'blob' }
    ),
};

export const supportApi = {
  getStaffUsers: () => api.get('/support/staff-users'),
  getChatConversations: (params?: { search?: string }) =>
    api.get('/support/chat/conversations', { params }),
  waitForChatUpdates: (params?: {
    search?: string;
    conversation_id?: number;
    list_after?: string;
    list_count?: number;
    conversation_after?: string;
  }) =>
    api.get('/support/chat/updates', {
      params,
      timeout: 30000,
    }),
  getChatConversationById: (id: number) =>
    api.get(`/support/chat/conversations/${id}`),
  sendChatMessage: (
    id: number,
    messageText: string,
    attachments?: ChatMessageAttachmentInput[]
  ) =>
    api.post(`/support/chat/conversations/${id}/messages`, {
      message_text: messageText,
      attachments: attachments || [],
    }),
  sendChatCallCommand: (
    id: number,
    action: 'request' | 'accept' | 'refuse' | 'end'
  ) => api.post(`/support/chat/conversations/${id}/call/${action}`, {}),
  sendChatWebRTCSignal: (
    id: number,
    signalType: 'offer' | 'answer' | 'ice_candidate',
    data: ChatWebRTCSignalInput
  ) =>
    api.post(`/support/chat/conversations/${id}/webrtc/${signalType}`, {
      sdp: data.sdp || '',
      candidate: data.candidate || '',
      sdp_mid: data.sdp_mid || '',
      sdp_mline_index:
        typeof data.sdp_mline_index === 'number' ? data.sdp_mline_index : -1,
    }),
  assignChatConversation: (id: number, assignedStaffId?: number | null) =>
    api.put(`/support/chat/conversations/${id}/assign`, {
      assigned_staff_id: assignedStaffId || 0,
    }),
  updateChatConversationStatus: (id: number, status: string) =>
    api.put(`/support/chat/conversations/${id}/status`, { status }),
  downloadChatAttachment: (messageId: number, attachmentId: number) =>
    api.get(
      `/support/chat/messages/${messageId}/attachments/${attachmentId}`,
      { responseType: 'blob' }
    ),
  getTickets: (params?: {
    search?: string;
    status?: string;
    assigned_staff_id?: string | number;
  }) => api.get('/support/tickets', { params }),
  getTicketById: (id: number) => api.get(`/support/tickets/${id}`),
  assignTicket: (id: number, assignedStaffId?: number | null) =>
    api.put(`/support/tickets/${id}/assign`, {
      assigned_staff_id: assignedStaffId || 0,
    }),
  replyToTicket: (id: number, messageText: string) =>
    api.post(`/support/tickets/${id}/replies`, { message_text: messageText }),
  updateTicketStatus: (id: number, status: string) =>
    api.put(`/support/tickets/${id}/status`, { status }),
  downloadAttachment: (ticketId: number, attachmentId: number) =>
    api.get(
      `/support/tickets/${ticketId}/attachments/${attachmentId}`,
      { responseType: 'blob' }
  ),
};

export const realtimeApi = {
  buildClientChatSocketUrl: () =>
    buildChatSocketUrl(localStorage.getItem('client_portal_token')),
  buildSupportChatSocketUrl: () =>
    buildChatSocketUrl(localStorage.getItem('token')),
  // Credentials TURN éphémères mintés par le backend (coturn du VPS) —
  // le secret ne quitte jamais le serveur.
  getSupportIceServers: () => api.get('/webrtc/ice-servers'),
};

// Licenses API
export const licensesApi = {
  getAll: (clientId?: number) => api.get('/licenses', { params: { client_id: clientId } }),
  generate: (data: GenerateLicenseInput) => api.post('/licenses/generate', data),
  update: (id: number, data: { client_id: number }) => api.put(`/licenses/${id}`, data),
  verify: (activationKey: string, hardwareId: string) =>
    api.post('/licenses/verify', { activation_key: activationKey, hardware_id: hardwareId }),
  deactivate: (id: number) => api.put(`/licenses/${id}/deactivate`),
  reactivate: (id: number) => api.post(`/licenses/reactivate/${id}`),
  delete: (id: number) => api.delete(`/licenses/${id}`),
  encodePassword: (text: string) => api.post('/licenses/encode-password', { text }),
};

// CNRC desktop licenses API
export const cnrcDesktopLicensesApi = {
  getAll: (status: string = 'all') =>
    api.get('/admin/cnrc-desktop-licenses', { params: { status, limit: 1000 } }),
  generate: (count: number) =>
    api.post('/admin/cnrc-desktop-licenses/generate', { count }),
  release: (id: number) =>
    api.post(`/admin/cnrc-desktop-licenses/${id}/release`, {}),
};

// Gestion des versions : catalogue des mises a jour des applications de bureau
export const desktopVersionsApi = {
  getAll: () => api.get('/admin/desktop-versions'),
  apercu: (produit: string, versionInstallee: string) =>
    api.get('/admin/desktop-versions/apercu', {
      params: { produit, version_installee: versionInstallee },
    }),
  create: (data: DesktopVersionInput) => api.post('/admin/desktop-versions', data),
  update: (id: number, data: DesktopVersionInput) =>
    api.put(`/admin/desktop-versions/${id}`, data),
  publish: (id: number) => api.post(`/admin/desktop-versions/${id}/publish`, {}),
  unpublish: (id: number) => api.post(`/admin/desktop-versions/${id}/unpublish`, {}),
  remove: (id: number) => api.delete(`/admin/desktop-versions/${id}`),
  // Le lot (palier 2) : ce qui est déposé, puis le rattachement. La taille et
  // le SHA-256 sont calculés PAR LE SERVEUR sur le fichier déposé.
  depot: (produit: string) =>
    api.get('/admin/desktop-versions/depot', { params: { produit } }),
  attacherLot: (id: number, data: DesktopLotInput) =>
    api.post(`/admin/desktop-versions/${id}/lot`, data),
  detacherLot: (id: number) => api.delete(`/admin/desktop-versions/${id}/lot`),
  // Nettoyage : versions que les postes ne voient plus, et leurs fichiers du dépôt.
  // produit '' = tous. L'aperçu ne touche à rien ; nettoyer supprime définitivement.
  apercuNettoyage: (produit: string) =>
    api.get('/admin/desktop-versions/nettoyage', { params: { produit } }),
  nettoyer: (produit: string) => api.post('/admin/desktop-versions/nettoyage', { produit }),
};

// Accès distant : abonnements DDNS des serveurs de clients
export const ddnsApi = {
  getAll: () => api.get('/admin/ddns'),
  create: (data: DdnsServeurInput) => api.post('/admin/ddns', data),
  update: (id: number, data: DdnsServeurInput) => api.put(`/admin/ddns/${id}`, data),
  renew: (id: number) => api.post(`/admin/ddns/${id}/renew`, {}),
  suspend: (id: number) => api.post(`/admin/ddns/${id}/suspend`, {}),
  resume: (id: number) => api.post(`/admin/ddns/${id}/resume`, {}),
  regenerateToken: (id: number) => api.post(`/admin/ddns/${id}/regenerate-token`, {}),
  remove: (id: number) => api.delete(`/admin/ddns/${id}`),
  // Jetons d'enrôlement et postes enrôlés d'un abonnement. Le jeton n'est rendu qu'à sa création
  // (`jeton`) ; révoquer est définitif et demande un motif.
  getJetonsPostes: (id: number) => api.get(`/admin/ddns/${id}/jetons-postes`),
  createJetonPostes: (id: number, data: DdnsJetonPostesInput) => api.post(`/admin/ddns/${id}/jetons-postes`, data),
  updateJetonPostes: (jetonId: number, data: DdnsJetonPostesInput) => api.put(`/admin/ddns-jetons/${jetonId}`, data),
  revokeJetonPostes: (jetonId: number, motif: string) => api.post(`/admin/ddns-jetons/${jetonId}/revoke`, { motif }),
  // Seul un jeton révoqué se supprime (409 sinon), avec les postes qu'il a fait entrer.
  removeJetonPostes: (jetonId: number) => api.delete(`/admin/ddns-jetons/${jetonId}`),
  approvePoste: (posteId: number) => api.post(`/admin/ddns-postes/${posteId}/approve`, {}),
  refusePoste: (posteId: number, motif: string) => api.post(`/admin/ddns-postes/${posteId}/refuse`, { motif }),
  revokePoste: (posteId: number, motif: string) => api.post(`/admin/ddns-postes/${posteId}/revoke`, { motif }),
  renamePoste: (posteId: number, libelle: string | null) => api.put(`/admin/ddns-postes/${posteId}`, { libelle }),
  // Seul un poste déjà coupé (révoqué, refusé, jeton révoqué) se supprime : 409 sinon.
  removePoste: (posteId: number) => api.delete(`/admin/ddns-postes/${posteId}`),
};

// Suivi des créances : constatations (étape 1), règlements (étape 2), soldes et relevés.
// Filtres facultatifs ; les montants partent en texte décimal exact, jamais en flottant.
export interface CreancesFiltres {
  client_id?: number;
  du?: string;
  au?: string;
}

// Paramètres vides retirés : le serveur lit '' comme absent, autant ne pas l'envoyer
const filtresRenseignes = (filtres: CreancesFiltres) =>
  Object.fromEntries(Object.entries(filtres).filter(([, valeur]) => valeur !== undefined && valeur !== '' && valeur !== 0));

export const creancesApi = {
  getConstatations: (filtres: CreancesFiltres = {}) =>
    api.get('/creances/constatations', { params: filtresRenseignes(filtres) }),
  createConstatation: (data: CreanceInput) => api.post('/creances/constatations', data),
  updateConstatation: (id: number, data: CreanceInput) => api.put(`/creances/constatations/${id}`, data),
  removeConstatation: (id: number) => api.delete(`/creances/constatations/${id}`),
  getReglements: (filtres: CreancesFiltres = {}) =>
    api.get('/creances/reglements', { params: filtresRenseignes(filtres) }),
  createReglement: (data: ReglementInput) => api.post('/creances/reglements', data),
  updateReglement: (id: number, data: ReglementInput) => api.put(`/creances/reglements/${id}`, data),
  removeReglement: (id: number) => api.delete(`/creances/reglements/${id}`),
  getSoldes: () => api.get('/creances/soldes'),
  getReleve: (clientId: number, periode: { du?: string; au?: string } = {}) =>
    api.get(`/creances/releve/${clientId}`, { params: filtresRenseignes(periode) }),
};

// Audit API
export const auditApi = {
  getHistory: (tableName: string, recordId: number) =>
    api.get(`/audit/${tableName}/${recordId}`),
};

// Admin API
export const adminApi = {
  // Users
  getUsers: () => api.get('/admin/users'),
  createUser: (data: { user_name: string; password: string; role_id?: number }) =>
    api.post('/admin/users', data),
  updateUser: (id: number, data: { user_name: string; password?: string; role_id?: number }) =>
    api.put(`/admin/users/${id}`, data),
  toggleActive: (id: number) => api.put(`/admin/users/${id}/toggle-active`, {}),

  // Sessions
  getSessions: (params?: { search?: string; token_type?: string; status?: string }) =>
    api.get('/admin/sessions', { params }),
  revokeSession: (id: string) => api.delete(`/admin/sessions/${encodeURIComponent(id)}`),

  // Roles
  getRoles: () => api.get('/admin/roles'),
  createRole: (data: { role_name: string; description: string; permissions: { entity: string; action: string }[] }) =>
    api.post('/admin/roles', data),
  updateRole: (id: number, data: { role_name: string; description: string; permissions: { entity: string; action: string }[] }) =>
    api.put(`/admin/roles/${id}`, data),
  deleteRole: (id: number) => api.delete(`/admin/roles/${id}`),

  // Permissions
  getPermissions: () => api.get('/admin/permissions'),

  // Webhook SMS (statut + import de l'historique depuis l'ancienne passerelle)
  getSmsWebhookStatus: () => api.get('/contrats/sms-webhook-status'),
  syncSmsHistory: (contratId?: number) =>
    api.post('/contrats/sync-sms-history', null, {
      params: contratId ? { contrat_id: contratId } : undefined,
      timeout: 600000,
    }),
};

// Types
export interface ContratInput {
  id_client: number;
  date_debut: string;
  date_fin: string;
  t_contrat: string;
  mode_reglt: string;
  reset_sms_counter?: boolean;
}

export interface ClientInput {
  client_name: string;
  telephone: string;
  email: string;
  wilaya: string;
  ville: string;
}

export interface ClientPortalProfileInput {
  client_name: string;
  telephone: string;
  email: string;
  wilaya: string;
  ville: string;
}

export interface ClientPortalTicketInput {
  subject: string;
  message_text: string;
  attachments?: ClientPortalTicketAttachmentInput[];
}

export interface ChatMessageAttachmentInput {
  file_name: string;
  content_type: string;
  file_size: number;
  base64_content: string;
}

export interface ChatWebRTCSignalInput {
  sdp?: string;
  candidate?: string;
  sdp_mid?: string;
  sdp_mline_index?: number;
}

export interface ClientPortalTicketAttachmentInput {
  file_name: string;
  content_type: string;
  file_size: number;
  base64_content: string;
}

export interface GenerateLicenseInput {
  client_id: number;
  hardware_id: string;
  license_type: string;
  notes?: string;
}

export default api;
